/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import SwaggerParser from '@apidevtools/swagger-parser';
import _ from 'lodash';
import { OpenAPIV3 } from 'openapi-types';
import * as yup from 'yup';

import {
  AuthenticationObject,
  TransformedResponseProperty,
  ServerVariableObject
} from '@ibm-watson/shared/ts/catalog';
import { Header } from '@ibm-watson/shared/ts/header';
import { createdObjectEvents } from '../../../ia/util/trackers/segment';
import { setImportErrors } from '../../store/catalog/reducer';
import { SUCCESS_PATH } from '../../util/constants';
import {
  AUTHENTICATION_METHODS,
  HEADER,
  OAUTH2_FLOW_TYPES,
  QUERY,
  RESPONSE_METHODS,
  SUPPORTED_OAUTH2_FLOWS,
  VALID_RESPONSE_METHODS,
  VALUE
} from './constants';
import { FIELDS } from './register/schema';

// Extend the Yup string schema with a custom method
interface StringSchemaWithIsAbsolute extends yup.StringSchema<string> {
  isAbsolute(): this;
}

export type AuthenticationMethods = {
  authenticationName: string;
  auth: AUTHENTICATION_METHODS;
};

export const getTagColor = {
  [RESPONSE_METHODS.GET]: 'blue',
  [RESPONSE_METHODS.POST]: 'green',
  [RESPONSE_METHODS.PUT]: 'purple',
  [RESPONSE_METHODS.DELETE]: 'red',
  [RESPONSE_METHODS.PATCH]: 'teal'
};

export const getAuthFriendlyName = {
  [AUTHENTICATION_METHODS.BASIC]: 'Basic auth',
  [AUTHENTICATION_METHODS.BEARER]: 'Bearer auth',
  [AUTHENTICATION_METHODS.API_KEY]: 'API key auth',
  [AUTHENTICATION_METHODS.NONE]: 'No authentication',
  [AUTHENTICATION_METHODS.OAUTH2]: 'OAuth 2.0'
};

export const getGrantFriendlyName = {
  [OAUTH2_FLOW_TYPES.AUTHORIZATION_CODE]: 'Authorization code',
  [OAUTH2_FLOW_TYPES.CLIENT_CREDENTIALS]: 'Client credentials',
  [OAUTH2_FLOW_TYPES.PASSWORD]: 'Password',
  [OAUTH2_FLOW_TYPES.CUSTOM]: 'Custom'
};

/**
 * From a given list of response properties, returns only those with the lowest 2XX status code.
 * TODO: At somepoint we need Actions to be able to support multiple 2XX responses AND non-200 responses and do their
 * own filtering. Also, we are filtering out responses that are in the header or the links object here and just
 * including the ones in the content object.
 *
 * @returns {array} Of properties.
 */
export const getFilteredResponses = (
  properties: TransformedResponseProperty[]
): TransformedResponseProperty[] => {
  // Filter properties for successful responses & ones that have a path defined or the "success" path.
  let filteredResponses = properties.filter(
    property =>
      property.path === SUCCESS_PATH ||
      (property.status.startsWith('2') && property.path)
  );
  let lowestNumSuccessKey = 0;
  properties.forEach(property => {
    if (property.status) {
      const statusNum = Number(property.status);
      if (lowestNumSuccessKey === 0 || statusNum < lowestNumSuccessKey) {
        lowestNumSuccessKey = statusNum;
      }
    }
  });
  // Filter responses again to contain the lowest 2XX response property only or the "success" path.
  filteredResponses = filteredResponses.filter(
    property =>
      property.path === SUCCESS_PATH ||
      property.status === lowestNumSuccessKey.toString()
  );
  return filteredResponses;
};

// Returns a string of a combination of the non-supported methods/types.
// Will be comprised of strings in HEAD, OPTIONS, and TRACE
export const getInvalidResponseMessagesFromSpec = (
  openApiSpec: OpenAPIV3.Document
): string[] => {
  const invalidResponses = [];
  const paths = openApiSpec?.paths as OpenAPIV3.PathsObject;
  for (const resource in paths) {
    for (const method in paths[resource]) {
      // Checking if operation method is valid
      const responseMethod = method.toUpperCase();
      if (
        RESPONSE_METHODS[responseMethod] &&
        !VALID_RESPONSE_METHODS.includes(responseMethod as RESPONSE_METHODS) &&
        !invalidResponses.includes(responseMethod as RESPONSE_METHODS)
      ) {
        invalidResponses.push(responseMethod);
      }
    }
  }
  return invalidResponses;
};

export const getInvalidResponseStr = (invalidResponses: string[]): string => {
  const invalidResponsesCopy = _.cloneDeep(invalidResponses);
  let invalidResponsesStr = invalidResponsesCopy?.join(', ') || '';
  // If there are multiple non-supported adds in the word 'and' to inline warning title
  if (invalidResponsesCopy.length > 1) {
    const lastCommaIndex = invalidResponsesStr.lastIndexOf(',');
    invalidResponsesStr =
      invalidResponsesStr.slice(0, lastCommaIndex) +
      ' and' +
      invalidResponsesStr.slice(lastCommaIndex + 1);
  }
  // If there are multiple non-supported adds in the word 'are' at the end otherwise adds 'is'
  // to complete the sentence ending in "not supported."
  if (invalidResponsesCopy.length > 1) {
    invalidResponsesStr = invalidResponsesStr + ' are';
  } else {
    invalidResponsesStr = invalidResponsesStr + ' is';
  }
  return invalidResponsesStr;
};

export const getAuthenticationMethods = (authentication: {
  [key: string]: OpenAPIV3.SecuritySchemeObject | OpenAPIV3.ReferenceObject;
}): AuthenticationMethods[] => {
  const authMethods: AuthenticationMethods[] = [
    { authenticationName: 'none', auth: AUTHENTICATION_METHODS.NONE }
  ];

  if (!authentication || _.isEmpty(authentication)) {
    return authMethods;
  }

  for (const [objectKey, objectVal] of Object.entries(authentication)) {
    const authScheme = (objectVal as OpenAPIV3.HttpSecurityScheme)?.scheme;
    const authType = (objectVal as OpenAPIV3.SecuritySchemeObject)?.type;

    if (authScheme === AUTHENTICATION_METHODS.BASIC_SCHEME) {
      authMethods.push({
        authenticationName: objectKey,
        auth: AUTHENTICATION_METHODS.BASIC
      });
    } else if (authScheme === AUTHENTICATION_METHODS.BEARER_SCHEME) {
      authMethods.push({
        authenticationName: objectKey,
        auth: AUTHENTICATION_METHODS.BEARER
      });
    } else if (authType === AUTHENTICATION_METHODS.API_KEY_TYPE) {
      authMethods.push({
        authenticationName: objectKey,
        auth: AUTHENTICATION_METHODS.API_KEY
      });
    } else if (authType === AUTHENTICATION_METHODS.OAUTH2) {
      authMethods.push({
        authenticationName: objectKey,
        auth: AUTHENTICATION_METHODS.OAUTH2
      });
    }
  }
  return authMethods;
};

export const parseSecuritySchemeAuth = (authentication: {
  [key: string]: OpenAPIV3.SecuritySchemeObject | OpenAPIV3.ReferenceObject;
}): AuthenticationObject => {
  if (_.isEmpty(authentication)) {
    return {};
  }

  const clonedAuth = _.cloneDeep(authentication);
  const auth: Partial<AuthenticationObject> = {};

  for (const key in clonedAuth) {
    const value = clonedAuth[key] as OpenAPIV3.SecuritySchemeObject;

    // Basic auth and bearer auth use the defined scheme
    // Api key auth and oauth2 use the defined type
    const scheme = (value as OpenAPIV3.HttpSecurityScheme).scheme;
    const type = value.type;

    if (type === AUTHENTICATION_METHODS.OAUTH2) {
      if (!_.isEmpty(auth[AUTHENTICATION_METHODS.OAUTH2])) {
        continue;
      }

      auth[AUTHENTICATION_METHODS.OAUTH2] = { flows: {} };
      const flows = value.flows;

      for (const flowKey of Object.keys(flows)) {
        const flow = flows[flowKey];

        switch (_.snakeCase(flowKey)) {
          case OAUTH2_FLOW_TYPES.PASSWORD:
            auth[AUTHENTICATION_METHODS.OAUTH2].flows[_.snakeCase(flowKey)] = {
              refresh_url: flow?.refreshUrl,
              scopes: flow?.scopes,
              token_url: flow?.tokenUrl
            };
            break;
          case OAUTH2_FLOW_TYPES.CLIENT_CREDENTIALS:
            auth[AUTHENTICATION_METHODS.OAUTH2].flows[_.snakeCase(flowKey)] = {
              refresh_url: flow?.refreshUrl,
              scopes: flow?.scopes,
              token_url: flow?.tokenUrl
            };
            break;
          case OAUTH2_FLOW_TYPES.AUTHORIZATION_CODE:
            auth[AUTHENTICATION_METHODS.OAUTH2].flows[_.snakeCase(flowKey)] = {
              authorization_url: flow?.authorizationUrl,
              refresh_url: flow?.refreshUrl,
              scopes: flow?.scopes,
              token_url: flow?.tokenUrl
            };
            break;
          default:
            break;
        }
      }

      continue;
    } else if (type === AUTHENTICATION_METHODS.API_KEY_TYPE) {
      auth[AUTHENTICATION_METHODS.BASIC] = {
        [value.name]: { type: VALUE }
      };
    } else if (scheme === AUTHENTICATION_METHODS.BASIC_SCHEME) {
      auth[AUTHENTICATION_METHODS.BASIC] = {
        username: { type: VALUE },
        password: { type: VALUE }
      };
    } else if (scheme === AUTHENTICATION_METHODS.BEARER_SCHEME) {
      auth[AUTHENTICATION_METHODS.BASIC] = {
        token: { type: VALUE }
      };
    }
  }

  return auth as AuthenticationObject;
};

export const parseSecuritySchemePrivateAuth = (authentication: {
  [key: string]: OpenAPIV3.SecuritySchemeObject | OpenAPIV3.ReferenceObject;
}): AuthenticationObject => {
  if (_.isEmpty(authentication)) {
    return {};
  }

  const clonedAuth = _.cloneDeep(authentication);
  const auth: Partial<AuthenticationObject> = {};

  for (const key in clonedAuth) {
    const value = clonedAuth[key] as OpenAPIV3.SecuritySchemeObject;

    const scheme = (value as OpenAPIV3.HttpSecurityScheme).scheme;
    const type = value.type;

    if (type === AUTHENTICATION_METHODS.API_KEY_TYPE) {
      if (_.isNil(auth[AUTHENTICATION_METHODS.API_KEY])) {
        auth[AUTHENTICATION_METHODS.API_KEY] = {};
      }

      auth[AUTHENTICATION_METHODS.API_KEY][value.name] = {
        type: VALUE,
        in: value.in
      };

      // This is skip past the switch-case in this iteration.
      continue;
    }

    switch (scheme) {
      case AUTHENTICATION_METHODS.BASIC_SCHEME:
        auth[AUTHENTICATION_METHODS.BASIC] = {
          password: { type: VALUE }
        };
        break;
      case AUTHENTICATION_METHODS.BEARER_SCHEME:
        auth[AUTHENTICATION_METHODS.BEARER] = {
          token: { type: VALUE }
        };
        break;
      default:
        break;
    }
  }

  return auth as AuthenticationObject;
};

export const setServerVariableDefaults = (serverVars: {
  [variable: string]: OpenAPIV3.ServerVariableObject;
}): { [variable: string]: ServerVariableObject } => {
  const cloneServerVars = _.cloneDeep(serverVars);
  for (const varName in cloneServerVars) {
    const varValue: Partial<ServerVariableObject> = cloneServerVars[varName];
    Object.assign(varValue, {
      value: varValue.default || ''
    });
  }
  // We need to manually override the TS type here.
  return cloneServerVars as unknown as {
    [variable: string]: ServerVariableObject;
  };
};

export const serverVariablesValid = (
  variables: ServerVariableObject[]
): boolean => {
  for (const varName in variables) {
    const varValue = variables[varName];
    if (
      varValue.value === '' ||
      _.isEmpty(varValue.value) ||
      /\s/.test(varValue.value)
    ) {
      return false;
    }
  }
  return true;
};

export const removeErrorCode = (message: string): string => {
  const errorCode = message.match(/^[E]{1}\d{9}[:]/);
  if (errorCode?.length === 1) {
    const splitError = message.split(errorCode[0]);
    return splitError[1];
  } else {
    return message;
  }
};

export const extractAuthorizationHeaderKeys = (auth: any): string[] => {
  const method = auth.authentication_method;
  const values = auth[method];

  const headerKeys: string[] = [];
  if (method === AUTHENTICATION_METHODS.BASIC) {
    headerKeys.push(Header.AUTHORIZATION);
  } else if (method === AUTHENTICATION_METHODS.BEARER) {
    headerKeys.push(Header.AUTHORIZATION);
  } else if (method === AUTHENTICATION_METHODS.API_KEY) {
    for (const apiKeyName of Object.keys(values)) {
      const { in: keyIsIn, type } = values[apiKeyName];
      if (keyIsIn === HEADER && type === VALUE) {
        headerKeys.push(apiKeyName);
      }
    }
  }

  return headerKeys;
};

export const extractAuthorizationQueryParameterKeys = (auth: any): string[] => {
  const method = auth.authentication_method;
  const values = auth[method];

  const paramKeys = [];
  if (method === AUTHENTICATION_METHODS.API_KEY) {
    for (const apiKeyName of Object.keys(values)) {
      const { in: keyIsIn, type } = values[apiKeyName];
      if (keyIsIn === QUERY && type === VALUE) {
        paramKeys.push(apiKeyName);
      }
    }
  }

  return paramKeys;
};

export const isCustomFlow = flow => flow?.startsWith('x-');

// The default flow names in OAuth2 schema authorizationCode, implicit, password
// clientCredentials can be converted into snake_case for our own purposes
// Do not snakeCase custom flow names which starts with x- as store does not accept it in snake_case
export const getFlowTypeName = (specFlow: string) => {
  if (isCustomFlow(specFlow)) {
    return specFlow;
  }
  return _.snakeCase(specFlow);
};

/**
 * Returns a space separated string of scope parameters for OAuth authentication code flow
 * based on the given scopes object.
 * @param {Object} scopesObj - An object representing the desired OAuth scopes. The object should
 * @returns {string} A space separated string of scope parameters for OAuth authentication code flow.
 * For example, the function will convert the following scopes object:
 *
 * {
 *  "scopes": {
 *     "user-read-private": "Can access to a user's private playlists",
 *     "user-read-email": "Can access to a user's email address",
 *   }
 * }
 * to the string -> "user-read-private user-read-email".
 */
export const getScopes = (scopesObj = {}) => {
  return (
    Object.keys(scopesObj)
      .map(scope => scope)
      .join(' ') || `''`
  );
};

export const isSupportedFlow = flow =>
  SUPPORTED_OAUTH2_FLOWS.includes(flow) || isCustomFlow(flow);

/**
 * Function for uploading an OpenAPI spec for a custom extension
 * @param params The state functions needed for the register flow or update flow
 */
export const onFileAdded = async params => {
  const {
    dispatch,
    addedFiles,
    setImporting,
    setImportFileName,
    transformOpenAPISpec,
    setUpdatedSpecBody,
    updateFieldValue
  } = params;
  dispatch(setImportErrors({ importErrors: [], importErrorSubject: '' }));
  if (Array.isArray(addedFiles) && addedFiles.length > 0) {
    setImporting(true);
    const file = addedFiles[0];
    setImportFileName(file.name);

    try {
      const fileReader = new FileReader();
      const specificationText: string = await new Promise((resolve, reject) => {
        fileReader.onload = e => resolve((e.target as any).result);
        fileReader.onerror = e => reject(e);
        fileReader.readAsText(file);
      });

      let specificationToSave: OpenAPIV3.Document = {} as OpenAPIV3.Document;
      let specificationToTest: OpenAPIV3.Document = {} as OpenAPIV3.Document;

      // We make sure the spec is valid on upload so we don't have
      // to check things again later.
      // Make sure the OpenAPI spec is valid JSON.
      try {
        // We need to do two of these because deepClone isn't
        // working on these objects for some reason and it running
        // the test mutations on them to ensure validity were
        // changing the original object even though it is passed
        // through deepClone first.
        specificationToSave = JSON.parse(specificationText);
        specificationToTest = JSON.parse(specificationText);
      } catch (error) {
        createdObjectEvents.customExtension(
          {
            objectType: 'customExtension',
            resultValue: 'File to upload must be in valid JSON format',
            successFlag: false
          },
          {
            waFileName: file.name
          }
        );
        throw {
          response: {
            data: {
              error: 'File to upload must be in valid JSON format',
              errors: []
            }
          }
        };
      }

      // Function to check if $ref is present at the top level within paths
      const hasImmediateRef = (obj: OpenAPIV3.Document['paths']): boolean => {
        if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
          return false;
        }

        // Check only immediate-level values for $ref, not deeply nested properties
        return Object.values(obj).some(
          value =>
            typeof value === 'object' && value !== null && '$ref' in value
        );
      };

      // Function to check for $ref in paths
      const hasRefInPaths = (data: OpenAPIV3.Document): boolean => {
        const { paths } = data;

        // Check for immediate-level $ref only in paths
        return hasImmediateRef(paths);
      };

      // To check if the file contains immediate $refs
      if (hasRefInPaths(specificationToTest)) {
        throw {
          response: {
            data: {
              error:
                'This OpenAPI spec file contains $refs in the "paths" property, which is not supported, please select a spec file without $refs and in the correct format'
            }
          }
        };
      }

      // Make sure the OpenAPI can be validated.
      try {
        const validatePromise = new Promise<void>((resolve, reject) => {
          SwaggerParser.validate(specificationToTest, null, err => {
            if (err) {
              reject(err);
            } else {
              resolve();
            }
          });
        });
        await validatePromise;
      } catch (error) {
        console.error(error);
        throw {
          response: {
            data: {
              error: 'The OpenAPI specification was invalid.',
              errors: [error]
            }
          }
        };
      }

      // Make sure the OpenAPI spec can be dereferenced.
      try {
        await SwaggerParser.dereference(specificationToTest, {
          continueOnError: false,
          // JSONSpec doesn't validate against a circular reference,
          // but IRL this would cause an infinite loop in our code.
          // Technically, an OpenAPI spec is allowed to contain a
          // circular reference if it is not required. Unfortunately,
          // our code is not writter in a way to handle this.
          // See https://github.ibm.com/watson-engagement-advisor/wea-backlog/issues/59532
          dereference: {
            circular: false
          }
          // SwaggerParser's ApiOptions type does not have
          // 'continueOnError' in its type, so we disable TS errors.
        } as unknown as any);
      } catch (error) {
        console.error(error);
        throw {
          response: {
            data: {
              error: 'The OpenAPI specification could not be dereferenced.',
              errors: [error]
            }
          }
        };
      }

      // Make sure the OpenAPI spec can be parsed.
      try {
        await transformOpenAPISpec(specificationToTest);
      } catch (error) {
        console.error(error);
        throw {
          response: {
            data: {
              error: 'The OpenAPI specification could not be parsed.',
              errors: [error]
            }
          }
        };
      }
      setUpdatedSpecBody(specificationToSave);
      updateFieldValue(FIELDS.SPECIFICATION_BODY, specificationToSave);
      setImporting(false);
    } catch (error) {
      dispatch(
        setImportErrors({
          importErrors: error.response.data.errors,
          importErrorSubject: error.response.data.error
        })
      );
      setImporting(false);
    }
  }
};

/**
 * Function for removing an OpenAPI spec from a custom extension spec upload
 * @param params The state functions needed for the register flow or update flow
 */
export const onFileRemoved = async params => {
  const { setImportFileName, dispatch, setUpdatedSpecBody, updateFieldValue } =
    params;
  setImportFileName('');
  dispatch(
    setImportErrors({
      importErrors: [],
      importErrorSubject: ''
    })
  );
  setUpdatedSpecBody(undefined);
  updateFieldValue(FIELDS.SPECIFICATION_BODY, undefined);
};

/**
 * Function for checking if the url is relative, and if yes build and return the absolute url using provided serverUrl.
 * This function is mainly used for OAuth2 auth code flow where tooling needs to redirect to auth code provider service.
 * @param url given url
 * @param serverUrl server uri to build absolute url
 */

export const getAbsoluteURL = (url: string, generatedServerUrl: string) => {
  // Define the custom Yup schema with the added method
  const schema: StringSchemaWithIsAbsolute = yup.string().test({
    name: 'isAbsolute',
    exclusive: true,
    test: value => {
      const parsedUrl = new URL(value);
      return !!parsedUrl.protocol;
    }
  }) as StringSchemaWithIsAbsolute;

  try {
    // Attempt to validate the url against the Yup schema.
    schema.validateSync(url);
    return url;
  } catch (error) {
    const absoluteUrl = new URL(generatedServerUrl);

    // NOTE cannot include queryParams in URL.pathname - set as URL.search
    // needed to support authUrl has query (see 68499)
    const qPos = url.indexOf('?');
    const urlSearch = qPos > -1 ? url.slice(qPos) : '';
    const urlPath = qPos > -1 ? url.slice(0, qPos) : url;

    // Remove any trailing / from server URL and append it with the relative path
    absoluteUrl.pathname = absoluteUrl.pathname.replace(/\/$/, '') + urlPath;
    if (urlSearch) {
      absoluteUrl.search = urlSearch;
    }
    return absoluteUrl.href;
  }
};
